import { v1 } from 'uuid';

import { LeaveMessageFormPayload, NewBotMessagePayload } from '../typedefs/actions';
import * as a from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import {
    BotConvoStartedPayload,
    ConversationSerialized,
    LocationSettingsPayload,
    PreMessage,
    TranscriptMessage,
} from '../typedefs/api';
import {
    Banner,
    InlineConfig,
    Link,
    LockedPricingBotConfig,
    SetVisitorDataConfig,
} from '../typedefs/external';

export const locationSettingsFetching = (attempts: number): a.LocationSettingsFetchingAction => ({
    type: actionTypes.LOCATION_SETTINGS_FETCHING,
    payload: { attempts },
});
export const locationSettingsFetched = (
    data: LocationSettingsPayload,
    inlineConfig: InlineConfig,
): a.LocationSettingsFetchedAction => ({
    type: actionTypes.LOCATION_SETTINGS_FETCHED,
    payload: { settings: data, inlineConfig },
});
export const locationSettingsFetchError = (): a.LocationSettingsFetchError => ({
    type: actionTypes.LOCATION_SETTINGS_FETCH_ERROR,
});
export const bootstrapStarted = (): a.BootstrapStartedAction => ({
    type: actionTypes.BOOTSTRAP_STARTED,
});
export const bootstrapFinished = (): a.BootstrapFinishedAction => ({
    type: actionTypes.BOOTSTRAP_FINISHED,
});
export const roxanneDataFetching = (): a.RoxanneDataFetchingAction => ({
    type: actionTypes.ROXANNE_DATA_FETCHING,
});
export const roxanneDataFetchError = (reason: string): a.RoxanneDataFetchErrorAction => ({
    type: actionTypes.ROXANNE_DATA_FETCH_ERROR,
    payload: { reason },
});

export const ctaClicked = (): a.CtaClickedAction => ({
    type: actionTypes.CTA_CLICKED,
    payload: { initialMessage: undefined },
});

export const startConvoWithMessage = (message: string): a.ConvoWithMessageStartRequestAction => ({
    type: actionTypes.CONVO_WITH_MESSAGE_START_REQUEST,
    payload: { initialMessage: message },
});

export const smsCtaClicked = (): a.SmsCtaClickedAction => ({
    type: actionTypes.SMS_CTA_CLICKED,
});

export const proactiveAdd = (
    displayMessage: string,
    invokeMessage: string,
): a.ProactiveAddAction => ({
    type: actionTypes.PROACTIVE_ADD,
    payload: { displayMessage, invokeMessage },
});

export const proactiveRemove = (): a.ProactiveRemoveAction => ({
    type: actionTypes.PROACTIVE_REMOVE,
});

export const proactiveClicked = (): a.ProactiveClickedAction => ({
    type: actionTypes.PROACTIVE_CLICKED,
});

export const proactiveClosed = (): a.ProactiveClosedAction => ({
    type: actionTypes.PROACTIVE_CLOSED,
});

export const botConvoStarting = (): a.BotConvoStartingAction => ({
    type: actionTypes.BOT_CONVO_STARTING,
});

export const convoStartError = (): a.ConvoStartErrorAction => ({
    type: actionTypes.CONVO_START_ERROR,
});

export const visitorBlacklisted = (): a.VisitorBlacklistedAction => ({
    type: actionTypes.VISITOR_BLACKLISTED,
});
export const botConvoStarted = (data: BotConvoStartedPayload): a.BotConvoStartedAction => ({
    type: actionTypes.BOT_CONVO_STARTED,
    payload: { ...data },
});

export const realConvoStarted = (): a.RealConvoStartedAction => ({
    type: actionTypes.REAL_CONVO_STARTED,
});

export const convoResumed = (): a.ConvoResumedAction => ({
    type: actionTypes.CONVO_RESUMED,
});

export const convoDetailsFetched = (
    conversation: ConversationSerialized,
): a.ConvoDetailsFetchedAction => ({
    type: actionTypes.CONVO_DETAILS_FETCHED,
    payload: { conversation },
});

export const convoTranscriptFetched = (
    messages: TranscriptMessage[],
): a.ConvoTranscriptFetchedAction => ({
    type: actionTypes.CONVO_TRANSCRIPT_FETCHED,
    payload: { messages },
});

export const setLocalSettings = (settings: any): a.LocalSettingsLoadedAction => ({
    type: actionTypes.LOCAL_SETTINGS_LOADED,
    payload: { ...settings },
});

export const sendMessage = (text: string, tempId?: string): a.SendMessageAction => ({
    type: actionTypes.SEND_MESSAGE,
    payload: { text, tempId: tempId || v1() },
});

export const messageSent = (
    tempId: string,
    realId: number,
    timestamp: string,
): a.MessageSentAction => ({
    type: actionTypes.MESSAGE_SENT,
    payload: { tempId, realId, timestamp },
});

export const messageSendError = (tempId: string): a.MessageSendErrorAction => ({
    type: actionTypes.MESSAGE_SEND_ERROR,
    payload: { tempId },
});

export const toggleMinimized = (): a.ToggleMinimizedAction => ({
    type: actionTypes.TOGGLE_MINIMIZED,
});

export const requestClose = (): a.ToggleRequestEndConvoAction => ({
    type: actionTypes.TOGGLE_REQUEST_END_CONVO,
});

export const endConvo = (): a.EndConvoAction => ({
    type: actionTypes.END_CONVO,
});

export const endConvoPending = (): a.EndConvoPendingAction => ({
    type: actionTypes.END_CONVO_PENDING,
});

export const endConvoSuccess = (): a.EndConvoSuccessAction => ({
    type: actionTypes.END_CONVO_SUCCESS,
});

export const setSurveyScore = (score: number): a.SetSurveyScoreAction => ({
    type: actionTypes.SET_SURVEY_SCORE,
    payload: { score },
});

export const surveyCompleted = (): a.SurveyCompletedAction => ({
    type: actionTypes.SURVEY_COMPLETED,
});

export const removeConversation = (hide: boolean = false): a.RemoveConversationAction => ({
    type: actionTypes.REMOVE_CONVERSATION,
    payload: { hide },
});

export const hideBanner = (): a.HideBannerAction => ({
    type: actionTypes.HIDE_BANNER,
});

export const requestMarkRead = (): a.RequestMarkReadActon => ({
    type: actionTypes.REQUEST_MARK_READ,
});

export const messagesMarkedRead = (
    messageIds: number[],
    fromType: 'agent' | 'visitor',
    fromId: number,
): a.MessagesMarkedReadAction => ({
    type: actionTypes.MESSAGES_MARKED_READ,
    payload: { messageIds, fromType, fromId },
});

export const changeTypingStatus = (status: 'composing' | 'paused'): a.TypingStatusChangeAction => ({
    type: actionTypes.TYPING_STATUS_CHANGE,
    payload: { status },
});

export const leaveMessageFormSubmit = (
    data: LeaveMessageFormPayload,
): a.LeaveMessageFormSubmitAction => ({
    type: actionTypes.LEAVE_MESSAGE_FORM_SUBMIT,
    payload: { ...data },
});

export const leaveMessageFormSubmitSuccess = (): a.LeaveMessageFormSubmitSuccessAction => ({
    type: actionTypes.LEAVE_MESSAGE_FORM_SUBMIT_SUCCESS,
});

export const leaveMessageFormSubmitError = (): a.LeaveMessageFormSubmitFailureAction => ({
    type: actionTypes.LEAVE_MESSAGE_FORM_SUBMIT_ERROR,
});

export const acceptVideoRequest = (): a.AcceptVideoRequestAction => ({
    type: actionTypes.ACCEPT_VIDEO_REQUEST,
});

export const startVideo = (
    primaryNode: HTMLVideoElement,
    secondaryNode: HTMLVideoElement,
): a.StartVideoAction => ({
    type: actionTypes.START_VIDEO,
    payload: { primaryNode, secondaryNode },
});

export const localVideoActive = (value: boolean): a.LocalVideoActiveAction => ({
    type: actionTypes.LOCAL_VIDEO_ACTIVE,
    payload: { value },
});

export const remoteVideoActive = (value: boolean): a.RemoteVideoActiveAction => ({
    type: actionTypes.REMOTE_VIDEO_ACTIVE,
    payload: { value },
});

export const remoteVideoStreaming = (value: boolean): a.RemoteVideoStreamingAction => ({
    type: actionTypes.REMOTE_VIDEO_STREAMING,
    payload: { value },
});

export const remoteVideoClosed = (): a.RemoteVideoClosedAction => ({
    type: actionTypes.REMOTE_VIDEO_CLOSED,
});

export const videoConnectionError = (error: string): a.VideoConnectionErrorAction => ({
    type: actionTypes.VIDEO_CONNECTION_ERROR,
    payload: { error },
});

export const closeVideoPanel = (): a.CloseVideoPanelAction => ({
    type: actionTypes.CLOSE_VIDEO_PANEL,
});

export const endVideo = (): a.EndVideoAction => ({
    type: actionTypes.END_VIDEO,
});

export const toggleAudioStream = (): a.ToggleAudioStreamAction => ({
    type: actionTypes.TOGGLE_AUDIO_STREAM,
});

export const toggleVideoStream = (): a.ToggleVideoStreamAction => ({
    type: actionTypes.TOGGLE_VIDEO_STREAM,
});

export const browserCapabilitiesFetched = (
    webRtcSupported: boolean,
): a.BrowserCapabilitiesFetchedAction => ({
    type: actionTypes.BROWSER_CAPABILITIES_FETCHED,
    payload: { webRtc: webRtcSupported },
});

export const startLockedPricingBot = (
    config: LockedPricingBotConfig,
): a.StartLockedPricingBotAction => ({
    type: actionTypes.START_LOCKED_PRICING_BOT,
    payload: config,
});

export const newBotMessage = (message: NewBotMessagePayload): a.NewBotMessageAction => ({
    type: actionTypes.NEW_BOT_MESSAGE,
    payload: message,
});

/**
 * used to simulate a bot typing event.  Only used on initial messages since we get an array of
 * messages to display all at once.  Other artificial delays are handled in the API.
 */
export const setBotTyping = (isTyping: boolean): a.TypingStatusAction => ({
    type: actionTypes.CLIENT_CHAT_STATUS_NOTIFICATION,
    payload: {
        channel: 'foo',
        status: isTyping ? 'composing' : 'paused',
        fromType: 'bot',
    },
});

export const contactInfoInjected = (
    contactInfo: SetVisitorDataConfig,
): a.ContactInfoGatheredAction => ({
    type: actionTypes.CONTACT_INFO_INJECTED,
    payload: contactInfo,
});

export const bannerInjected = (banners: Banner[]): a.BannerInjectedAction => ({
    type: actionTypes.BANNER_INJECTED,
    payload: { banners },
});

export const linkInjected = (links: Link[]): a.LinkInjectedAction => ({
    type: actionTypes.LINK_INJECTED,
    payload: { links },
});

export const hideCta = (): a.HideCtaAction => ({
    type: actionTypes.HIDE_CTA,
});

export const setCtaText = (ctaText: string): a.SetCtaTextAction => ({
    type: actionTypes.SET_CTA_TEXT,
    payload: { ctaText },
});

export const customMetadataInjected = (customMetadata: string): a.CustomMetadatInjectedAction => ({
    type: actionTypes.CUSTOM_METADATA_INJECTED,
    payload: { customMetadata },
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/actions/index.ts